/** @typedef {'idle'|'connecting'|'open'|'reconnecting'|'disconnected'} SocketStatus */

import { filterSocialImConversations, isCustomerServiceConversation } from './socialImConversations';

export const initialChatState = {
  conversations: [],
  conversationsLoading: false,
  conversationsError: '',
  activeConversationId: null,
  messagesByConversation: {},
  participantSummaries: {},
  readStates: {},
  composerDrafts: {},
  replyTargets: {},
  editTargets: {},
  optimisticMessages: {},
  failedMessages: {},
  picker: { open: false, tab: 'stickers', query: '', data: null, loading: false },
  socketStatus: /** @type {SocketStatus} */ ('idle'),
  gapFillPending: {},
  /** @type {Record<string, Record<string, number>>} conversationId → memberId → expiresAtMs */
  typingByConversation: {},
  /** @type {Record<string, string|null>} conversationId → serverMessageId to highlight */
  highlightByConversation: {},
  /** @type {Record<string, Array>} conversationId → pinned message summaries */
  pinsByConversation: {},
  /** @type {Record<string, string[]>} conversationId → favorited serverMessageIds */
  favoriteIdsByConversation: {},
};

export function createEmptyTimeline() {
  return {
    items: [],
    hasMoreBefore: false,
    oldestSeq: null,
    newestSeq: null,
    loading: false,
    loadingMore: false,
    error: '',
    initialized: false,
  };
}

export function chatReducer(state, action) {
  switch (action.type) {
    case 'SOCKET_STATUS':
      return { ...state, socketStatus: action.status };

    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.conversationId };

    case 'CONVERSATIONS_LOADING':
      return { ...state, conversationsLoading: true, conversationsError: '' };

    case 'CONVERSATIONS_LOADED':
      return {
        ...state,
        conversationsLoading: false,
        conversations: filterSocialImConversations(action.conversations),
        conversationsError: '',
      };

    case 'CONVERSATIONS_ERROR':
      return {
        ...state,
        conversationsLoading: false,
        conversationsError: action.error || '載入會話失敗',
      };

    case 'UPSERT_CONVERSATION': {
      const conv = action.conversation;
      if (!conv) return state;
      // 客服與社交 IM inbox 隔離：不得寫入一般會話列表
      if (isCustomerServiceConversation(conv)) {
        return state;
      }
      const id = conv.conversationId || conv.id;
      const list = [...state.conversations];
      const idx = list.findIndex((c) => String(c.conversationId || c.id) === String(id));
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...conv };
      } else {
        list.unshift(conv);
      }
      list.sort((a, b) => {
        const ta = new Date(a.lastMessageAt || a.lastMessageTime || 0).getTime();
        const tb = new Date(b.lastMessageAt || b.lastMessageTime || 0).getTime();
        return tb - ta;
      });
      return { ...state, conversations: list };
    }

    case 'SUMMARIES_MERGED':
      return {
        ...state,
        participantSummaries: { ...state.participantSummaries, ...action.summaries },
      };

    case 'TIMELINE_LOADING': {
      const id = String(action.conversationId);
      const prev = state.messagesByConversation[id] || createEmptyTimeline();
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: {
            ...prev,
            loading: !action.more,
            loadingMore: !!action.more,
            error: '',
          },
        },
      };
    }

    case 'TIMELINE_ERROR': {
      const id = String(action.conversationId);
      const prev = state.messagesByConversation[id] || createEmptyTimeline();
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: { ...prev, loading: false, loadingMore: false, error: action.error || '載入失敗' },
        },
      };
    }

    case 'TIMELINE_SET': {
      const id = String(action.conversationId);
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: {
            items: action.items || [],
            hasMoreBefore: !!action.hasMoreBefore,
            oldestSeq: action.oldestSeq ?? null,
            newestSeq: action.newestSeq ?? null,
            loading: false,
            loadingMore: false,
            error: '',
            initialized: true,
          },
        },
      };
    }

    case 'TIMELINE_PREPEND': {
      const id = String(action.conversationId);
      const prev = state.messagesByConversation[id] || createEmptyTimeline();
      const merged = mergeMessageLists(action.items || [], prev.items);
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: {
            ...prev,
            items: merged,
            hasMoreBefore: !!action.hasMoreBefore,
            oldestSeq: action.oldestSeq ?? prev.oldestSeq,
            newestSeq: prev.newestSeq ?? action.newestSeq,
            loading: false,
            loadingMore: false,
            error: '',
            initialized: true,
          },
        },
      };
    }

    case 'TIMELINE_APPEND': {
      const id = String(action.conversationId);
      const prev = state.messagesByConversation[id] || createEmptyTimeline();
      const merged = mergeMessageLists(prev.items, action.items || []);
      const newest = merged.length ? merged[merged.length - 1].seq : prev.newestSeq;
      const oldest = merged.length ? merged[0].seq : prev.oldestSeq;
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: {
            ...prev,
            items: merged,
            oldestSeq: oldest,
            newestSeq: newest,
            loading: false,
            loadingMore: false,
            initialized: true,
          },
        },
      };
    }

    case 'MESSAGE_UPSERT': {
      const msg = action.message;
      if (!msg) return state;
      const id = String(msg.conversationId);
      const prev = state.messagesByConversation[id] || createEmptyTimeline();
      const items = upsertMessage(prev.items, msg);
      const newest = items.length ? items[items.length - 1].seq : prev.newestSeq;
      const oldest = items.length ? items[0].seq : prev.oldestSeq;
      const optimistic = { ...(state.optimisticMessages[id] || {}) };
      if (msg.clientMessageId) {
        delete optimistic[msg.clientMessageId];
      }
      const failed = { ...(state.failedMessages[id] || {}) };
      if (msg.clientMessageId) {
        delete failed[msg.clientMessageId];
      }
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: {
            ...prev,
            items,
            oldestSeq: oldest,
            newestSeq: newest ?? prev.newestSeq,
            initialized: true,
          },
        },
        optimisticMessages: { ...state.optimisticMessages, [id]: optimistic },
        failedMessages: { ...state.failedMessages, [id]: failed },
      };
    }

    case 'MESSAGE_REACTIONS_PATCH': {
      const { conversationId, serverMessageId, reactions } = action;
      if (conversationId == null || !serverMessageId) return state;
      const id = String(conversationId);
      const targetId = String(serverMessageId);
      const prev = state.messagesByConversation[id] || createEmptyTimeline();
      const items = (prev.items || []).map((m) =>
        m.serverMessageId != null && String(m.serverMessageId) === targetId
          ? { ...m, reactions: reactions || [] }
          : m
      );
      return {
        ...state,
        messagesByConversation: {
          ...state.messagesByConversation,
          [id]: { ...prev, items },
        },
      };
    }

    case 'OPTIMISTIC_ADD': {
      const msg = action.message;
      const id = String(msg.conversationId);
      const map = { ...(state.optimisticMessages[id] || {}) };
      map[msg.clientMessageId] = { ...msg, _localStatus: 'sending' };
      const failed = { ...(state.failedMessages[id] || {}) };
      delete failed[msg.clientMessageId];
      return {
        ...state,
        optimisticMessages: { ...state.optimisticMessages, [id]: map },
        failedMessages: { ...state.failedMessages, [id]: failed },
      };
    }

    case 'OPTIMISTIC_FAIL': {
      const { conversationId, clientMessageId, error } = action;
      const id = String(conversationId);
      const optimistic = { ...(state.optimisticMessages[id] || {}) };
      const msg = optimistic[clientMessageId];
      delete optimistic[clientMessageId];
      const failed = { ...(state.failedMessages[id] || {}) };
      if (msg) {
        failed[clientMessageId] = { ...msg, _localStatus: 'failed', _error: error || '發送失敗' };
      }
      return {
        ...state,
        optimisticMessages: { ...state.optimisticMessages, [id]: optimistic },
        failedMessages: { ...state.failedMessages, [id]: failed },
      };
    }

    case 'FAILED_CLEAR': {
      const id = String(action.conversationId);
      const failed = { ...(state.failedMessages[id] || {}) };
      delete failed[action.clientMessageId];
      return { ...state, failedMessages: { ...state.failedMessages, [id]: failed } };
    }

    case 'DRAFT_SET': {
      const id = String(action.conversationId);
      return {
        ...state,
        composerDrafts: { ...state.composerDrafts, [id]: action.draft ?? '' },
      };
    }

    case 'REPLY_SET': {
      const id = String(action.conversationId);
      return {
        ...state,
        replyTargets: { ...state.replyTargets, [id]: action.replyTo || null },
        // 編輯與回覆互斥
        editTargets: action.replyTo
          ? { ...state.editTargets, [id]: null }
          : state.editTargets,
      };
    }

    case 'EDIT_SET': {
      const id = String(action.conversationId);
      return {
        ...state,
        editTargets: { ...state.editTargets, [id]: action.editTarget || null },
        replyTargets: action.editTarget
          ? { ...state.replyTargets, [id]: null }
          : state.replyTargets,
        composerDrafts: {
          ...state.composerDrafts,
          [id]: action.editTarget?.text ?? state.composerDrafts[id] ?? '',
        },
      };
    }

    case 'READ_STATES_SET': {
      const id = String(action.conversationId);
      return {
        ...state,
        readStates: {
          ...state.readStates,
          [id]: {
            lastMessageSeq: action.lastMessageSeq,
            participants: action.participants || [],
          },
        },
      };
    }

    case 'READ_UPDATED': {
      const { conversationId, memberId, lastReadSeq, lastMessageSeq } = action;
      const id = String(conversationId);
      const prev = state.readStates[id] || { participants: [], lastMessageSeq: 0 };
      const participants = [...(prev.participants || [])];
      const idx = participants.findIndex((p) => String(p.memberId) === String(memberId));
      const row = { memberId, lastReadSeq, updatedAt: new Date().toISOString() };
      if (idx >= 0) participants[idx] = { ...participants[idx], ...row };
      else participants.push(row);

      const conversations = state.conversations.map((c) => {
        if (String(c.conversationId || c.id) !== id) return c;
        // Only update my unread when event is for me — caller sets myMemberId
        if (action.myMemberId != null && String(memberId) === String(action.myMemberId)) {
          const unread = Math.max(0, (lastMessageSeq ?? c.lastMessageSeq ?? 0) - lastReadSeq);
          return {
            ...c,
            myLastReadSeq: lastReadSeq,
            unreadCount: unread,
            hasUnreadMention: unread > 0 ? c.hasUnreadMention : false,
          };
        }
        return c;
      });

      return {
        ...state,
        readStates: {
          ...state.readStates,
          [id]: {
            lastMessageSeq: lastMessageSeq ?? prev.lastMessageSeq,
            participants,
          },
        },
        conversations,
      };
    }

    case 'PICKER_SET':
      return { ...state, picker: { ...state.picker, ...action.patch } };

    case 'TYPING_SET': {
      const cid = String(action.conversationId);
      const mid = String(action.memberId);
      if (!cid || !mid || mid === 'undefined' || mid === 'null') return state;
      const prevConv = { ...(state.typingByConversation[cid] || {}) };
      if (action.typing === false) {
        delete prevConv[mid];
      } else {
        const expiresAtMs =
          action.expiresAtMs != null
            ? Number(action.expiresAtMs)
            : Date.now() + 5000;
        if (!Number.isFinite(expiresAtMs) || expiresAtMs <= Date.now()) {
          delete prevConv[mid];
        } else {
          prevConv[mid] = expiresAtMs;
        }
      }
      return {
        ...state,
        typingByConversation: {
          ...state.typingByConversation,
          [cid]: prevConv,
        },
      };
    }

    case 'TYPING_CLEAR_MEMBER': {
      const cid = String(action.conversationId);
      const mid = String(action.memberId);
      const prevConv = { ...(state.typingByConversation[cid] || {}) };
      if (!(mid in prevConv)) return state;
      delete prevConv[mid];
      return {
        ...state,
        typingByConversation: {
          ...state.typingByConversation,
          [cid]: prevConv,
        },
      };
    }

    case 'HIGHLIGHT_MESSAGE': {
      const cid = String(action.conversationId);
      return {
        ...state,
        highlightByConversation: {
          ...state.highlightByConversation,
          [cid]: action.serverMessageId || null,
        },
      };
    }

    case 'PINS_SET': {
      const cid = String(action.conversationId);
      return {
        ...state,
        pinsByConversation: {
          ...state.pinsByConversation,
          [cid]: Array.isArray(action.pins) ? action.pins : [],
        },
      };
    }

    case 'FAVORITES_SET': {
      const cid = String(action.conversationId);
      return {
        ...state,
        favoriteIdsByConversation: {
          ...state.favoriteIdsByConversation,
          [cid]: Array.isArray(action.serverMessageIds) ? action.serverMessageIds.map(String) : [],
        },
      };
    }

    default:
      return state;
  }
}

export function messageKey(msg) {
  if (!msg) return '';
  if (msg.serverMessageId) return `s:${msg.serverMessageId}`;
  if (msg.clientMessageId) return `c:${msg.clientMessageId}`;
  if (msg.seq != null) return `q:${msg.seq}`;
  return '';
}

export function upsertMessage(items, incoming) {
  const list = Array.isArray(items) ? [...items] : [];
  const key = messageKey(incoming);
  let idx = -1;
  if (incoming.serverMessageId) {
    idx = list.findIndex((m) => m.serverMessageId === incoming.serverMessageId);
  }
  if (idx < 0 && incoming.clientMessageId) {
    idx = list.findIndex((m) => m.clientMessageId === incoming.clientMessageId);
  }
  if (idx >= 0) {
    list[idx] = { ...list[idx], ...incoming, _localStatus: 'sent' };
  } else if (key) {
    list.push({ ...incoming, _localStatus: incoming._localStatus || 'sent' });
  }
  return sortBySeq(list);
}

export function mergeMessageLists(a, b) {
  const map = new Map();
  [...(a || []), ...(b || [])].forEach((m) => {
    const k = messageKey(m);
    if (!k) return;
    const prev = map.get(k);
    if (!prev) {
      map.set(k, m);
      return;
    }
    // Prefer server fields
    map.set(k, { ...prev, ...m });
  });
  // Also collapse client+server pairs
  const byClient = new Map();
  const values = [...map.values()];
  values.forEach((m) => {
    if (m.clientMessageId && m.serverMessageId) {
      byClient.set(m.clientMessageId, m.serverMessageId);
    }
  });
  const filtered = values.filter((m) => {
    if (m.serverMessageId) return true;
    if (m.clientMessageId && byClient.has(m.clientMessageId)) return false;
    return true;
  });
  return sortBySeq(filtered);
}

function sortBySeq(list) {
  return [...list].sort((a, b) => {
    const sa = a.seq == null ? Number.MAX_SAFE_INTEGER : a.seq;
    const sb = b.seq == null ? Number.MAX_SAFE_INTEGER : b.seq;
    if (sa !== sb) return sa - sb;
    return String(a.clientMessageId || '').localeCompare(String(b.clientMessageId || ''));
  });
}

export function createClientMessageId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
